// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
// covered-by: frontends/carrie/src/ask-v2/project/__tests__/openItemRowTitle.test.ts
import { type AskPunttOpenItem } from "@mg/schemas/src/christo/askPunttOpenItems";

import i18n from "../../i18n/config";
import { claimOf } from "../review/rememberedSentence";

/** Must match `ownerName` fallback in `AskV2ProjectPage.tsx`. */
export const ASK_V2_PROJECT_OWNER_NAME_FALLBACK = "Owner";

const TITLE_LIMIT = 80;
// why: a word-boundary cut that keeps less than this is a stub, not a title.
const MIN_WORD_CUT = TITLE_LIMIT / 2;

function recordedTitle(item: AskPunttOpenItem): string {
  const title = (item as { title?: unknown }).title;
  return typeof title === "string" ? title.trim() : "";
}

export function openItemShortNeeded(item: AskPunttOpenItem): string {
  const needed = item.needed.trim();
  if (item.kind !== "unjudged_finding") return needed;
  // why: `claimOf` drops the remediation and the markers, so a finding body
  // that carries nothing but a fix -- "**Fix:** Add the panel." -- or only the
  // markers themselves strips down to "". That emptied the row title AND the
  // question line under it, leaving a clickable open item with no text at all
  // (and a blank entry in the mark-done warning). The no-needed label is the
  // fallback for "no readable claim", not merely for an empty payload.
  const claim = claimOf(needed);
  if (claim.length > 0) return claim;
  return claimOf(
    i18n.t("askV2.openItems.unjudgedFindingNoNeeded", { ns: "ui" }),
  );
}

function questionText(item: AskPunttOpenItem): string {
  return openItemShortNeeded(item);
}

function firstSentence(text: string): string {
  const sentence = text.split(/(?<=[.!?])\s+/)[0] ?? text;
  return sentence.trim();
}

function shorten(text: string): string {
  if (text.length <= TITLE_LIMIT) return text;
  const head = text.slice(0, TITLE_LIMIT);
  // why: stepping back to a word boundary is only needed when the limit lands
  // INSIDE a word. When the next character is whitespace the head already ends
  // on a whole word, and stepping back anyway threw away a word that fit.
  const splitsWord = !/\s/.test(text.charAt(TITLE_LIMIT));
  const cut = splitsWord ? head.replace(/\s\S*$/, "") : head;
  // why: one unbroken token longer than the limit -- a long file name, a link --
  // makes that word-boundary cut collapse to the first word or two, titling a
  // whole question "Confirm". A hard cut still reads as the question; a stub
  // does not. The empty-cut case (no whitespace in the head) falls out here.
  const kept = (cut.trim().length >= MIN_WORD_CUT ? cut : head).trim();
  return kept.replace(/[,;:]$/, "");
}

// why: the type is already the group dropdown, and the rollup has no title column — a payload title wins, otherwise the first sentence of the question, never the kind label.
export function openItemRowTitle(item: AskPunttOpenItem): string {
  const titled = recordedTitle(item);
  if (titled.length > 0) return titled;
  const question = questionText(item);
  if (question.length === 0) return "";
  return shorten(firstSentence(question));
}
